import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getModuleData } from '../../utils/dataGenerator';
import { BoxIcon } from 'lucide-react';
interface GlassCardProps {
  id: string;
  title: string;
  agentRole: string;
  subtitle: string;
  icon: BoxIcon;
  isActive: boolean;
  onClick: () => void;
  onOpenModule?: () => void;
  color: 'cyan' | 'pink' | 'purple';
}
const moduleRoutes: Record<string, string> = {
  safety: '/safety',
  classroom: '/classroom',
  growth: '/growth',
  knowledge: '/knowledge',
  'ai-tutor': '/ai-tutor'
};
export function GlassCard({
  id,
  title,
  agentRole,
  subtitle,
  icon: Icon,
  isActive,
  onClick,
  onOpenModule,
  color
}: GlassCardProps) {
  const navigate = useNavigate();
  const [data, setData] = useState(getModuleData(id));
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    if (isActive || isHovered) {
      const interval = setInterval(() => {
        setData(getModuleData(id));
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isActive, isHovered, id]);
  const colorClasses = {
    cyan: {
      text: 'text-[#38BDF8]',
      border: 'border-[#38BDF8]/30',
      shadow: 'shadow-[#38BDF8]/20',
      bg: 'bg-[#38BDF8]',
      glow: 'shadow-[0_0_20px_rgba(56,189,248,0.3)]'
    },
    pink: {
      text: 'text-[#F472B6]',
      border: 'border-[#F472B6]/30',
      shadow: 'shadow-[#F472B6]/20',
      bg: 'bg-[#F472B6]',
      glow: 'shadow-[0_0_20px_rgba(244,114,182,0.3)]'
    },
    purple: {
      text: 'text-[#A78BFA]',
      border: 'border-[#A78BFA]/30',
      shadow: 'shadow-[#A78BFA]/20',
      bg: 'bg-[#A78BFA]',
      glow: 'shadow-[0_0_20px_rgba(167,139,250,0.3)]'
    }
  };
  const colors = colorClasses[color];
  const activeClass = isActive ? `scale-110 z-20 ${colors.border} glass-card-active ${colors.glow}` : 'scale-100 z-10 glass-panel hover:bg-white/5';
  const handleCardClick = () => {
    onClick();
    // 点击卡片直接跳转到对应页面
    const route = moduleRoutes[id];
    if (route) {
      navigate(route);
    }
  };
  return <div className={`relative w-64 h-80 rounded-2xl transition-all duration-500 ease-out cursor-pointer group flex flex-col items-center justify-center p-6 ${activeClass}`} onClick={handleCardClick} onMouseEnter={() => {
    setIsHovered(true);
    onClick();
  }} onMouseLeave={() => setIsHovered(false)} data-id="element-58">
      {/* Data Bubble */}
      <div className={`absolute -top-24 left-1/2 -translate-x-1/2 w-48 bg-[#0B1120]/90 backdrop-blur-md border ${colors.border} rounded-lg p-3 transition-all duration-500 ${isActive || isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`} data-id="element-59">
        <div className="text-center" data-id="element-60">
          <div className="text-xs text-gray-400 mb-1" data-id="element-61">{data.label}</div>
          <div className={`text-xl font-mono font-bold ${colors.text}`} data-id="element-62">
            {data.value}
          </div>
          <div className="h-px w-full bg-white/10 my-2" data-id="element-63"></div>
          <div className="flex justify-between text-xs" data-id="element-64">
            <span className="text-gray-400" data-id="element-65">{data.subLabel}</span>
            <span className="text-white" data-id="element-66">{data.subValue}</span>
          </div>
        </div>
        <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-[#0B1120]/90`} data-id="element-67"></div>
      </div>

      {/* Icon Container */}
      <div className={`relative mb-4 p-4 rounded-full transition-all duration-500 ${isActive ? `bg-white/10 ${colors.glow}` : 'bg-white/5'}`} data-id="element-68">
        <Icon size={48} className={`transition-all duration-500 ${isActive ? 'scale-110' : 'group-hover:scale-110'} ${colors.text}`} strokeWidth={1.5} data-id="element-69" />
        <div className={`absolute inset-0 rounded-full blur-xl opacity-0 transition-opacity duration-500 ${isActive ? 'opacity-40' : 'group-hover:opacity-20'} ${colors.bg}`} data-id="element-70"></div>
      </div>

      {/* Text Content */}
      <h3 className={`text-xl font-bold mb-1 text-center transition-colors duration-300 ${isActive ? 'text-white' : 'text-gray-200'}`} data-id="element-71">
        {title}
      </h3>
      <p className={`text-sm font-medium mb-2 text-center ${colors.text}`} data-id="element-72">
        {agentRole}
      </p>
      <p className="text-xs text-center text-gray-400 leading-relaxed px-2" data-id="element-73">
        {subtitle}
      </p>

      {/* Hover Hint */}
      <div className={`mt-6 overflow-hidden transition-all duration-300 ${isActive || isHovered ? 'max-h-10 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-74">
        <div className={`text-xs text-center ${colors.text} font-medium`} data-id="element-75">
          点击进入系统
        </div>
      </div>

      {/* Decorative corners */}
      <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-white/20 rounded-tl-lg" data-id="element-76"></div>
      <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-white/20 rounded-tr-lg" data-id="element-77"></div>
      <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-white/20 rounded-bl-lg" data-id="element-78"></div>
      <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-white/20 rounded-br-lg" data-id="element-79"></div>
    </div>;
}